// Vocabulario de dominio usado pelo CTA (listas/cartoes), Mapa Arboreo e Area Piloto.
// Sao codigos e rotulos do cadastro, nao dados: os valores reais vem da API.

export interface OptionItem {
  value: string;
  label: string;
}

/** Tipos de individuo aceitos por cta.tree_individuals.individual_type (CHECK da migration 004). */
export const tiposIndividuoOptions: OptionItem[] = [
  { value: "ARVORE", label: "Árvore" },
  { value: "PALMEIRA", label: "Palmeira" },
  { value: "MUDA", label: "Muda" },
  { value: "ARBUSTO_PORTE_ARBOREO", label: "Arbusto de porte arbóreo" },
  { value: "TOCO", label: "Toco" },
  { value: "INDIVIDUO_MORTO", label: "Indivíduo morto" },
  { value: "NAO_IDENTIFICADO", label: "Não identificado" },
];

/** Classes de risco aceitas por cta.tree_risk_assessments.risk_class. */
export const classesRiscoOptions: OptionItem[] = [
  { value: "BAIXO", label: "Baixo" },
  { value: "MEDIO", label: "Médio" },
  { value: "ALTO", label: "Alto" },
  { value: "CRITICO", label: "Crítico" },
];

/** Rotulos conhecidos de review_status; codigos desconhecidos sao exibidos humanizados. */
const reviewStatusLabels: Record<string, string> = {
  PENDENTE: "Pendente",
  EM_REVISAO: "Em revisão",
  REVISADO: "Revisado",
  VALIDADO: "Validado",
  REJEITADO: "Rejeitado",
  ARQUIVADO: "Arquivado",
};

export const reviewStatusBadge: Record<string, string> = {
  PENDENTE: "bg-caution-100 text-caution-700",
  EM_REVISAO: "bg-warning-100 text-warning-700",
  REVISADO: "bg-secondary-100 text-secondary-700",
  VALIDADO: "bg-primary-100 text-primary-700",
  REJEITADO: "bg-danger-100 text-danger-700",
  ARQUIVADO: "bg-background-100 text-foreground-500",
};

/** "EM_REVISAO" -> "Em revisao" quando o codigo nao estiver catalogado. */
export function humanizeCode(code: string): string {
  const s = code.replace(/_/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function tipoIndividuoLabel(code: string | null | undefined): string {
  if (!code) return "—";
  return tiposIndividuoOptions.find((o) => o.value === code)?.label ?? humanizeCode(code);
}

export function riscoLabel(code: string | null | undefined): string {
  if (!code) return "Sem avaliação";
  return classesRiscoOptions.find((o) => o.value === code.toUpperCase())?.label ?? humanizeCode(code);
}

export function reviewStatusLabel(code: string | null | undefined): string {
  if (!code) return "—";
  return reviewStatusLabels[code.toUpperCase()] ?? humanizeCode(code);
}

/** Cor do marcador por classe de risco; sem avaliacao de risco fica cinza. */
export function riscoColor(code: string | null | undefined): string {
  switch ((code ?? "").toUpperCase()) {
    case "BAIXO": return "#16a34a";
    case "MEDIO": return "#eab308";
    case "ALTO": return "#f97316";
    case "CRITICO": return "#dc2626";
    default: return "#9ca3af";
  }
}

/** Codigo da area piloto real do Setor Bela Vista (migration 039). */
export const SBV_AREA_PILOTO_CODE = "SBV-REAL-001";

/** Centro de referencia do Setor Bela Vista, usado so enquanto nao ha geometria carregada. */
export const SBV_CENTER: [number, number] = [-16.7145, -49.262];

/**
 * Niveis qualitativos das inspecoes territoriais (FITA): ilha de calor, deficit arboreo.
 * Os pacotes trazem variantes (ALTA/ALTO, MEDIO_ALTO, MUITO_ALTO, CRITICA/CRITICO).
 */
export function nivelSeveridade(code: string | null | undefined): 0 | 1 | 2 | 3 | 4 | null {
  if (!code) return null;
  const c = code.toUpperCase();
  if (c.startsWith("CRITIC")) return 4;
  if (c === "MUITO_ALTO" || c === "MUITO_ALTA" || c.startsWith("ALT") || c === "HIGH") return 3;
  if (c === "MEDIO_ALTO" || c === "MEDIA_ALTA") return 2;
  if (c.startsWith("MEDI") || c === "MEDIUM") return 1;
  if (c.startsWith("BAIX") || c === "LOW") return 0;
  return null;
}

export function nivelBadge(code: string | null | undefined): string {
  switch (nivelSeveridade(code)) {
    case 4: return "bg-danger-100 text-danger-700";
    case 3: return "bg-warning-100 text-warning-700";
    case 2: return "bg-caution-100 text-caution-700";
    case 1: return "bg-caution-50 text-caution-700";
    case 0: return "bg-primary-100 text-primary-700";
    default: return "bg-background-100 text-foreground-500";
  }
}

export function nivelColor(code: string | null | undefined): string {
  switch (nivelSeveridade(code)) {
    case 4: return "#dc2626";
    case 3: return "#f97316";
    case 2: return "#f59e0b";
    case 1: return "#eab308";
    case 0: return "#16a34a";
    default: return "#9ca3af";
  }
}

/** Ilha de calor alta ou critica (nivel >= 3). */
export function ilhaDeCalorAlta(code: string | null | undefined): boolean {
  const n = nivelSeveridade(code);
  return n !== null && n >= 3;
}
